import React, { useState } from 'react'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import copyToClipboardFallback from 'copy-to-clipboard'
import { Button } from 'react-bootstrap'
import errorNotifier from '../utils/errorNotifier'

const COPY_FAILURE_MESSAGE = "Couldn't copy to your clipboard. Reveal the secret and copy it manually."

// Tries navigator.clipboard first, then falls back to the copy-to-clipboard package
// (execCommand-based). Resolves to whether the copy succeeded; never rejects.
const copyText = async (text) => {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    try {
      await navigator.clipboard.writeText(text)
      return true
    } catch (err) {
      // fall through to the execCommand-based fallback below
    }
  }
  return copyToClipboardFallback(text)
}

/**
 * Copies `text` (known synchronously), or fetches the value via `getText` first when given
 * (`getText` takes precedence). `getText` must handle its own error reporting and reject on
 * failure -- nothing is copied and nothing is notified here in that case. A failure to copy
 * an already-fetched value is this component's own responsibility to surface.
 */
const CopyButton = ({
  text, getText, variant = 'outline-secondary', size, label = '',
  icon = 'fa-copy', title = 'copy to clipboard'
}) => {
  const [copying, setCopying] = useState(false)

  if (getText) {
    const handleClick = async () => {
      setCopying(true)
      let value
      try {
        value = await getText()
      } catch (err) {
        // getText is responsible for reporting the error; nothing to copy
        setCopying(false)
        return
      }
      try {
        if (value) {
          const copied = await copyText(value)
          if (!copied) throw new Error('Copy to clipboard failed')
        }
      } catch (err) {
        await errorNotifier(err, COPY_FAILURE_MESSAGE)
      } finally {
        setCopying(false)
      }
    }

    return (
      <Button
        variant={variant}
        title={title}
        size={size}
        disabled={copying}
        onClick={handleClick}
      >
        <i className={`fa ${copying ? 'fa-spinner fa-spin' : icon}`} />
        <span>{label}</span>
      </Button>
    )
  }

  return (
    <CopyToClipboard text={text}>
      <Button
        variant={variant}
        title={title}
        size={size}
        disabled={!text || text.length === 0}
      >
        <i className={`fa ${icon}`} />
        <span>{label}</span>
      </Button>
    </CopyToClipboard>
  )
}

export default CopyButton
